import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { UserPlus, RotateCcw, Send, Trash2, AlertTriangle, X } from 'lucide-react'
import { listUsers, inviteUser, createUser, updateUser, resetUserPassword, resendInvite, deleteUser, AppUser } from '../services/api'

const ROLES = ['viewer', 'operator', 'admin']

export default function UsersPanel() {
  const qc = useQueryClient()
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState('viewer')
  const [msg, setMsg] = useState<string | null>(null)
  const [pendingDelete, setPendingDelete] = useState<AppUser | null>(null)
  // Utility/service-account (no email) direct-create form
  const [uUser, setUUser] = useState('')
  const [uPass, setUPass] = useState('')
  const [uRole, setURole] = useState('viewer')

  const { data: users } = useQuery({ queryKey: ['users'], queryFn: async () => (await listUsers()).data })
  const invalidate = () => qc.invalidateQueries({ queryKey: ['users'] })

  const invite = useMutation({
    mutationFn: () => inviteUser(email, name || null, role),
    onSuccess: () => { setEmail(''); setName(''); setMsg('Invitation sent.'); invalidate() },
    onError: (e: any) => setMsg(e?.response?.data?.detail ?? 'Invite failed'),
  })
  const createUtil = useMutation({
    mutationFn: () => createUser(uUser.trim(), uPass, uRole, null),
    onSuccess: (r) => { setUUser(''); setUPass(''); setMsg(`Utility account "${r.data.username}" created.`); invalidate() },
    onError: (e: any) => setMsg(e?.response?.data?.detail ?? 'Create failed'),
  })
  const edit = useMutation({
    mutationFn: (v: { id: number; body: any }) => updateUser(v.id, v.body),
    onSuccess: invalidate,
    onError: (e: any) => setMsg(e?.response?.data?.detail ?? 'Update failed'),
  })
  const reset = useMutation({ mutationFn: (id: number) => resetUserPassword(id), onSuccess: () => setMsg('Reset link sent.') })
  const resend = useMutation({ mutationFn: (id: number) => resendInvite(id), onSuccess: () => setMsg('Invite resent.') })
  const del = useMutation({
    mutationFn: (id: number) => deleteUser(id),
    onSuccess: () => { setMsg('User deleted.'); invalidate() },
    onError: (e: any) => setMsg(e?.response?.data?.detail ?? 'Delete failed'),
  })

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Users</h2>
      <p className="text-sm text-gray-600 mb-4">Invite employees and manage their roles. Viewer = read-only, Operator = + refresh, Admin = full access.</p>
      {msg && <div className="text-sm text-gray-600 mb-3">{msg}</div>}

      <div className="flex flex-wrap items-end gap-2 mb-6">
        <input className="px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="email@alamobb.net"
          value={email} onChange={(e) => setEmail(e.target.value)} />
        <input className="px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="Full name"
          value={name} onChange={(e) => setName(e.target.value)} />
        <select className="px-3 py-2 border border-gray-300 rounded-md text-sm" value={role} onChange={(e) => setRole(e.target.value)}>
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <button onClick={() => email && invite.mutate()} disabled={!email || invite.isPending}
          className="flex items-center px-4 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50">
          <UserPlus className="h-4 w-4 mr-1" /> Invite
        </button>
      </div>

      <div className="border-t border-gray-200 pt-4 mb-6">
        <p className="text-sm font-medium text-gray-800 mb-1">Utility account (no email)</p>
        <p className="text-xs text-gray-500 mb-2">For service/API logins (e.g. an integration). You set the password; the account is active immediately. It has no email, so it can't use forgot-password — reset it by re-creating or via the DB.</p>
        <div className="flex flex-wrap items-end gap-2">
          <input className="px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="username"
            value={uUser} onChange={(e) => setUUser(e.target.value)} />
          <input type="password" autoComplete="new-password" className="px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="password (min 8)"
            value={uPass} onChange={(e) => setUPass(e.target.value)} />
          <select className="px-3 py-2 border border-gray-300 rounded-md text-sm" value={uRole} onChange={(e) => setURole(e.target.value)}>
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <button onClick={() => createUtil.mutate()} disabled={!uUser.trim() || uPass.length < 8 || createUtil.isPending}
            className="flex items-center px-4 py-2 text-sm text-white bg-gray-700 rounded-md hover:bg-gray-800 disabled:opacity-50">
            <UserPlus className="h-4 w-4 mr-1" /> Create
          </button>
        </div>
      </div>

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50"><tr>
            {['User', 'Role', 'Status', 'Last Login', ''].map((h) => (
              <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
            ))}
          </tr></thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {(users ?? []).map((u: AppUser) => (
              <tr key={u.id}>
                <td className="px-4 py-2 text-sm">
                  <div className="font-medium text-gray-900">{u.full_name ?? u.email ?? u.username}</div>
                  <div className="text-gray-400 text-xs">{u.email ?? u.username}</div>
                </td>
                <td className="px-4 py-2 text-sm">
                  <select value={u.role} onChange={(e) => edit.mutate({ id: u.id, body: { role: e.target.value } })}
                    className="border border-gray-200 rounded px-1 py-0.5 text-sm">
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
                <td className="px-4 py-2 text-sm">
                  {u.pending ? <span className="text-amber-600">pending</span>
                    : u.is_active ? <button className="text-green-600" onClick={() => edit.mutate({ id: u.id, body: { is_active: false } })}>active</button>
                    : <button className="text-gray-400" onClick={() => edit.mutate({ id: u.id, body: { is_active: true } })}>disabled</button>}
                </td>
                <td className="px-4 py-2 text-sm text-gray-600">{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : '—'}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  {u.pending
                    ? <button title="Resend invite" onClick={() => resend.mutate(u.id)} className="text-primary-600 hover:text-primary-800"><Send className="h-4 w-4" /></button>
                    : <button title="Send reset link" onClick={() => reset.mutate(u.id)} className="text-gray-500 hover:text-gray-700"><RotateCcw className="h-4 w-4" /></button>}
                  <button title="Delete user"
                    onClick={() => setPendingDelete(u)}
                    className="text-red-500 hover:text-red-700 ml-3"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pendingDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => !del.isPending && setPendingDelete(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <AlertTriangle className="h-5 w-5 text-red-600 mr-2" /> Delete user
              </h2>
              <button onClick={() => setPendingDelete(null)} disabled={del.isPending}
                className="p-1.5 hover:bg-gray-100 rounded-full disabled:opacity-50"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-5 text-sm text-gray-700">
              <p>
                Permanently delete{' '}
                <span className="font-semibold">{pendingDelete.full_name || pendingDelete.email || pendingDelete.username}</span>
                {pendingDelete.full_name && pendingDelete.email && (
                  <span className="text-gray-400"> ({pendingDelete.email})</span>
                )}?
              </p>
              <p className="text-gray-500 mt-2">This permanently removes the account and cannot be undone.</p>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <button onClick={() => setPendingDelete(null)} disabled={del.isPending}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">
                Cancel
              </button>
              <button onClick={() => del.mutate(pendingDelete.id, { onSettled: () => setPendingDelete(null) })}
                disabled={del.isPending}
                className="px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50">
                {del.isPending ? 'Deleting…' : 'Delete user'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
